import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../../_app';
import React from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { fetchEmailFinderAIPrompts, fetchLoadingDataWith } from '@/lib/service';
import { page3WithWebApplicationJsonLd } from '@/lib/jsonld';
import { pageTypeEnum } from '@/components/email-finder/page-client';

const PageMainContent = dynamic(() => import('@/components/email-finder/page-client'), {
  ssr: true,
});

const WEB_APPLICATION_MAP = {
  hashtag: {
    name: 'Instagram Hashtag Email Finder',
    url: 'https://www.dolphinradar.com/instagram-email-finder/hashtag',
    applicationCategory: 'Instagram Email finder',
    description:
      'Instagram Hashtag Email Scraper helps you collect verified emails from hashtag communities. Reach engaged users and expand your brand’s reach.',
    featureList: [
      'scrape emails by hashtag',
      'IG Email Scraper',
      'instagram email extractor',
      'ig email extractor',
      'instagram email finder',
    ],
  },
  keyword: {
    name: 'Instagram Keyword Email Finder',
    url: 'https://www.dolphinradar.com/instagram-email-finder/keyword',
    applicationCategory: 'Instagram Email finder',
    description:
      'Find verified Instagram emails using keyword-powered search. Discover hidden leads, influencers, and niche audiences ready for outreach.',
    featureList: [
      'instagram email finder',
      'instagram account email finder',
      'find instagram email',
      'Scrape Emails from keyword',
      'IG email scraper',
    ],
  },
  location: {
    name: 'Instagram Location Email Finder',
    url: 'https://www.dolphinradar.com/instagram-email-finder/location',
    applicationCategory: 'Instagram Email finder',
    description:
      'Collect verified Instagram emails by city, region, or country. Turn location URLs into contact lists for local campaigns and audience outreach.',
    featureList: [
      'Scrape Emails by Location',
      'Scrape Emails by Hashtag',
      'find instagram by email',
      'find email instagram',
      'instagram email finder',
    ],
  },
  followers: {
    name: 'Instagram Followers Email Finder',
    url: 'https://www.dolphinradar.com/instagram-email-finder/followers',
    applicationCategory: 'Instagram Email finder',
    description:
      'Instagram Followers Email Scraper finds public emails from followers of any profile. Build outreach lists from proven, interest-driven audiences.',
    featureList: [
      'Scrape Emails from Keyword',
      'Scrape Emails by Location',
      'Scrape Emails from Follower Lists',
      'Scrape Emails from Following Lists',
      'find instagram email',
    ],
  },
  followings: {
    name: 'Instagram Followings Email Finder',
    url: 'https://www.dolphinradar.com/instagram-email-finder/followings',
    applicationCategory: 'Instagram Email finder',
    description:
      'Scrape verified emails from the accounts your prospects follow. Connect directly with influencers, experts, and brands that shape their decisions.',
    featureList: [
      'scrape emails from following lists',
      'find email by instagram',
      'find email instagram',
      'ig email extractor',
      'instagram email finder',
    ],
  },
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl, params } = context;
  const { id } = params as { id: string };

  if (id.toLowerCase() === 'profile') {
    return {
      redirect: {
        destination: `/instagram-email-finder`,
        permanent: true,
      },
    };
  }

  const path = `/instagram-email-finder/${id}`;

  const [localeRes, tdkRes, dataRes, emailFinderAIPromptsRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale),
    fetchEmailFinderAIPrompts(),
  ]);

  if (!dataRes?.data) {
    return {
      redirect: {
        destination: `/`,
        permanent: false,
      },
    };
  }
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

  const jsonLd = page3WithWebApplicationJsonLd(
    {
      title: tdkRes?.data.title || '',
      desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    {
      url: siteUrl,
    },
    WEB_APPLICATION_MAP[id.toString().toLowerCase() as keyof typeof WEB_APPLICATION_MAP]
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Anonymous_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Anonymously_Meta_Desc ?? null,
      contentData: dataRes?.data ?? null,
      jsonLd,
      id: id,
      emailFinderAIPrompts: emailFinderAIPromptsRes?.data || [],
    },
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  console.log(props);
  return <PageMainContent key={(props as { id: string }).id} data={props} type={pageTypeEnum.subPage} />;
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
